import { LocationType, NotificationType } from "../../../apis/networkdashboard/graphql/graphql";
import { HealthRecord } from "../../../apis/stats/rest/models";

export type { IFilter } from "../../../helpers/types";

export interface SearchableSubscription {
  disruption: NotificationType | undefined;
  health: HealthRecord;
  subscription: {
    customerDescription: string;
    subscriptionId: string;
    locations?: LocationType[];
    tags: string[];
  };
}

const getFilteredStatus = <T extends SearchableSubscription>(activeFilters: string[], subscriptions: T[]) =>
  subscriptions.filter((s) => activeFilters.includes(s.health.status));

const getFilteredTags = <T extends SearchableSubscription>(activeTags: string[], subscriptions: T[]) =>
  subscriptions.filter((s) => s.subscription.tags.some((tag) => activeTags.includes(tag)));

const getFilteredLocation = <T extends SearchableSubscription>(activeLocations: string[], subscriptions: T[]) => {
  const normalizedLocations = activeLocations.map((loc) => loc.toLowerCase());
  return subscriptions.filter((s) => matchSubscriptionByLocation(s, normalizedLocations) ?? false);
};

const matchSubscriptionByLocation = (subscription: SearchableSubscription, normalizedLocations: string[]) =>
  subscription.subscription.locations?.some((l) => {
    const locationStr = `${l.address?.city?.toLowerCase()} (${l.address?.street?.toLowerCase()} ${l.address?.number})`;
    normalizedLocations.some((loc) => locationStr === loc);
  });

const getFilteredQuery = <T extends SearchableSubscription>(query: string, subscriptions: T[]): T[] =>
  subscriptions.filter((s) => {
    const searchQuery = query.toLowerCase();
    const name = s.subscription.customerDescription.toLowerCase();
    return name.includes(searchQuery) || s.subscription.subscriptionId.includes(searchQuery);
  });

export const getFilteredItems = <T extends SearchableSubscription>(
  items: T[],
  activeFilters: string[],
  activeTags: string[],
  activeLocations: string[],
  query: string,
) => {
  let filteredSubscriptions = items;

  if (activeFilters.length > 0) {
    filteredSubscriptions = getFilteredStatus(activeFilters, filteredSubscriptions);
  }
  if (activeTags.length > 0) {
    filteredSubscriptions = getFilteredTags(activeTags, filteredSubscriptions);
  }
  if (activeLocations.length > 0) {
    filteredSubscriptions = getFilteredLocation(activeLocations, filteredSubscriptions);
  }
  if (query) {
    filteredSubscriptions = getFilteredQuery(query, filteredSubscriptions);
  }

  return filteredSubscriptions;
};
